import {
  IOrganization,
  IOrganizationBrief,
  IOrganizationDisplay,
  IUser,
  Maybe,
} from '../../../types/types.generated';
import { IDropdown } from '../../../types/common';
import { IEditUserData, IUserForm } from '../types/userForm';

export const initUserForm: IUserForm = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  pid: '',
  status: '',
  title: '',
  division: '',
  department: '',
  manager: '',
  isInternalUser: false,
  isSyncManaged: false,
  organizationIds: [],
  accessPolicyIds: [],
  disableAutomatedEmail: false,
};

export const initAcceptedEmailTlds: string[] = [
  '.com',
  '.edu',
  '.org',
  '.net',
  '.biz',
  '.info',
  '.gov',
  '.mil',
  '.pro',
];

export const acceptedEmailTlds: string[] = [];

export const mapOrgsToDropdown = (
  orgs: IOrganization[] | IOrganizationDisplay[]
): IDropdown[] =>
  orgs.map((org) => ({
    id: org.id,
    name: org.name,
    label: org.name,
  }));

export const setInitialAcceptedEmailDomains = () => {
  if (!acceptedEmailTlds.length) {
    initAcceptedEmailTlds.forEach((domain) => acceptedEmailTlds.push(domain));
    return;
  }
  acceptedEmailTlds.forEach((domain, index) => {
    if (!initAcceptedEmailTlds.includes(domain)) {
      acceptedEmailTlds.splice(index, 1);
    }
  });
};

/**
 * Add domain to allowed email domains based on user's organization
 */
export const setCustomEmailDomain = (
  orgDomain: string,
  selectedOrgId: string
) => {
  const orgEmailDomain = orgDomain;
  if (orgEmailDomain) {
    const orgEmailTLD = orgEmailDomain
      .toLowerCase()
      .substring(orgEmailDomain.indexOf('.'));
    if (acceptedEmailTlds.indexOf(selectedOrgId) === -1) {
      acceptedEmailTlds.push(orgEmailTLD);
    }
  }
};

export const mapUserToForm = (
  user: IUser,
  charterOrgId?: string
): IEditUserData => {
  const {
    id,
    pid,
    firstName,
    lastName,
    email,
    phone,
    accessPolicyIds,
    organizationIds,
    isInternalUser,
    isSyncManaged,
    status,
    title,
    manager,
    division,
    department,
  } = user;

  return {
    ...(isInternalUser && { pid: pid || '' }),
    id,
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
    phone: phone || '',
    accessPolicyIds: accessPolicyIds?.length ? accessPolicyIds : [],
    organizationIds: organizationIds?.length
      ? organizationIds.filter((orgId) => orgId !== charterOrgId)
      : [],
    isInternalUser: isInternalUser ?? false,
    isSyncManaged: isSyncManaged || false,
    status: status || '',
    title: title || '',
    manager: manager || '',
    department: department || '',
    division: division || '',
  };
};

export const getUserOrgById = (
  allOrgs: IOrganizationBrief[],
  orgId: string
): Maybe<IOrganizationBrief> => {
  const userOrg = allOrgs.find((org) => org.id === orgId);
  if (!userOrg) {
    return null;
  }

  return userOrg;
};

export const getUserOrgs = (
  allOrgs: IOrganizationBrief[],
  userOrgIds: string[]
): IOrganizationBrief[] => {
  const userOrgs: IOrganizationBrief[] = [];
  userOrgIds.forEach((orgId) => {
    const userOrg = getUserOrgById(allOrgs, orgId);
    if (!userOrg) {
      return;
    }

    userOrgs.push(userOrg);
  });

  return userOrgs;
};

export const requestSupportLink = {
  href: 'https://jira.charter.com/secure/CreateIssue.jspa?pid=34302&issuetype=50200',
  text: 'Request Support',
};

export const OrgErrorMsg = {
  nonStandardOrg:
    'There are registration restrictions with this organization type. External users from this organization with user management permission can invite new users or you can request support.',
  smbOrg:
    "This organization is a Spectrum Business customer. Users can't be assigned to it.",
  smbOrgNewMsg:
    "The organization you are trying to assign is a Spectrum Business customer. Users can't be assigned to it.",
};
